import { useState } from "react";
import { Input } from "../components/ui/input";
import { PageHeader, SectionCard, KpiCard, Loader, EmptyState, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { money, dateTimeFmt, todayStr, daysAgoStr, TZ } from "../lib/api";

const KIND_CLS = { Sale: "text-emerald-400", "Credit sale": "text-slate-400", "Credit payment": "text-emerald-400", "Credit purchase": "text-slate-400", "Supplier payment": "text-rose-400", "Stock purchase": "text-blue-400", Expense: "text-rose-400" };

export default function Cashflow() {
  const { depotParam, scopeLabel } = useDepot();
  const [start, setStart] = useState(daysAgoStr(6));
  const [end, setEnd] = useState(todayStr());
  const { data } = useApi("/cashflow", { start, end, depot_id: depotParam, tz: TZ });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Cashflow Ledger" subtitle="Every dollar in (sales) and out (stock purchases, expenses) with a running balance."
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-9 w-[150px] bg-slate-900" data-testid="cashflow-start-input" />
            <span className="text-slate-500">→</span>
            <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[150px] bg-slate-900" data-testid="cashflow-end-input" />
          </div>
        } />
      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Opening balance" value={money(data.opening)} tone="slate" testid="cashflow-opening" />
            <KpiCard label="Cash in" value={money(data.total_in)} tone="emerald" testid="cashflow-total-in" delay={50} />
            <KpiCard label="Cash out" value={money(data.total_out)} tone="rose" testid="cashflow-total-out" delay={100} />
            <KpiCard label="Closing balance" value={money(data.closing)} tone="amber" testid="cashflow-closing" delay={150} />
          </div>
          <SectionCard title={`${data.entries.length} entries`}>
            {!data.entries.length ? <EmptyState text="No cash activity in this period" testid="cashflow-empty" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cashflow-table">
                  <thead className={TABLE_HEAD}>
                    <tr>
                      <th className="px-4 py-2.5 text-left">Time</th><th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Description</th><th className="px-4 py-2.5 text-left">Depot</th>
                      <th className="px-4 py-2.5 text-right">In</th><th className="px-4 py-2.5 text-right">Out</th>
                      <th className="px-4 py-2.5 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((r) => (
                      <tr key={r.id} className={TABLE_ROW}>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(r.created_at)}</td>
                        <td className={`px-4 py-2.5 text-xs font-mono uppercase ${KIND_CLS[r.kind]}`}>{r.kind}</td>
                        <td className="px-4 py-2.5"><div>{r.description}</div><div className="text-xs text-slate-500">{[r.party, r.payment_method, r.user_name].filter(Boolean).join(" · ")}</div></td>
                        <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{r.depot_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-400">{r.cash_in ? money(r.cash_in) : ""}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-rose-400">{r.cash_out ? money(r.cash_out) : ""}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold">{money(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
